/**
 * Utility helpers to provide user-friendly Auth0 error messages.
 */

export interface Auth0ErrorInfo {
  message: string;
  description: string;
  action: string;
  docsUrl?: string;
}

// Common Auth0 error mappings and suggested actions
export const auth0ErrorMap: Record<string, Auth0ErrorInfo> = {
  'no_connections_enabled_for_the_organization': {
    message: 'No connections enabled for the organization',
    description: 'The organization does not have any authentication methods configured (Google, database, etc.).',
    action: 'Contact the administrator to enable at least one authentication connection for the organization.',
    docsUrl: 'https://auth0.com/docs/get-started/auth0-overview/create-organizations',
  },
  'organization_not_found': {
    message: 'Organization not found',
    description: 'The specified organization does not exist or is not available.',
    action: 'Verify that the organization ID is correct.',
  },
  'access_denied': {
    message: 'Access denied',
    description: 'You do not have permission to access this organization.',
    action: 'Request access from the organization administrator.',
  },
  'unauthorized': {
    message: 'Unauthorized',
    description: 'Your credentials are invalid or the session has expired.',
    action: 'Please sign in again.',
  },
  'login_required': {
    message: 'Login required',
    description: 'You must sign in to access this resource.',
    action: 'You will be redirected to the login screen automatically.',
  },
};

export const getAuth0ErrorInfo = (error: Error): Auth0ErrorInfo => {
  const errorMessage = error.message.toLowerCase();
  
  // Try to match the underlying Auth0 error with a known pattern
  for (const [key, info] of Object.entries(auth0ErrorMap)) {
    if (errorMessage.includes(key.replace(/_/g, ' ')) || errorMessage.includes(key)) {
      return info;
    }
  }
  
  // Fallback message if there is no direct match
  return {
    message: 'Authentication error',
    description: error.message,
    action: 'Try again or contact an administrator for assistance.',
  };
};
